// @ts-check
// Abas Geral, Identificação e Avaliação de Configurações > Atendimento (contrato 10 de atendimento, seções 3.4.4, 5, 14 e
// 24.4): janela de reabertura, fechamento automático, aviso de transferência padrão, lembrete de consulta e a finalidade
// dos números (com link para Canais); tentativas e validade do código e o modelo de autenticação do WhatsApp; a
// avaliação por departamento com a pergunta e o pedido de comentário.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Link } from '../../app/router.js';
import { listChannels } from '../../data/conversations.js';
import { getServiceDeskSettings, listServiceTeams, updateServiceDeskSettings, updateServiceTeamSettings } from '../../data/service-desk.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { useSendableTemplates } from '../conversations/template-picker.js';
import { Button } from '../../ui/button.js';
import { Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { serviceErrorMessage } from './service-display.js';
import { t } from '../../i18n/index.js';

/**
 * Campo numérico inteiro com limites.
 * @param {{ label: string, hint?: string, value: number, min: number, max: number, onChange: (value: number) => void, disabled?: boolean, suffix?: string }} props
 */
export function NumberField({ label, hint, value, min, max, onChange, disabled = false, suffix }) {
  return html`<label class="form-field">
    <span class="form-field-label">${label}</span>
    <span class="service-number-field">
      <input class="text-input" type="number" min=${min} max=${max} step="1" value=${String(value)} disabled=${disabled}
        onInput=${(/** @type {Event} */ event) => onChange(Number(/** @type {HTMLInputElement} */ (event.currentTarget).value))} />
      ${suffix ? html`<span class="service-number-suffix">${suffix}</span>` : null}
    </span>
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </label>`;
}

/** @param {{ message: string | null }} props */
function FormError({ message }) {
  return message ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${message}</span></div>` : null;
}

/** Lê e salva a configuração geral do atendimento (PATCH parcial). */
function useServiceDeskSettings() {
  const [settings, setSettings] = useState(/** @type {Record<string, any> | null} */ (null));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    getServiceDeskSettings().then(setSettings).catch((requestError) => setError(serviceErrorMessage(requestError)));
  }, []);
  /** @param {Record<string, any>} patch */
  const save = async (patch) => {
    setIsSaving(true);
    setError(null);
    try {
      setSettings(await updateServiceDeskSettings(patch));
      showSuccessSnackbar(t('serviceSettings.saved'));
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return { settings, setSettings, error, isSaving, save };
}

/** @param {{ canEdit: boolean }} props */
export function ServiceSettingsGeneralTab({ canEdit }) {
  const { settings, setSettings, error, isSaving, save } = useServiceDeskSettings();
  const [channels, setChannels] = useState(/** @type {any[] | null} */ (null));
  const [teams, setTeams] = useState(/** @type {any[]} */ ([]));
  useEffect(() => {
    listChannels().then((data) => setChannels(data.channels ?? [])).catch(() => setChannels([]));
    listServiceTeams().then(setTeams).catch(() => setTeams([]));
  }, []);
  if (!settings) return html`<${Skeleton} height=${200} radius=${8} />`;
  /** @param {string} key @param {any} value */
  const set = (key, value) => setSettings({ ...settings, [key]: value });
  return html`<div class="service-settings-tab-body" data-testid="service-settings-general">
    <${SettingsSection} title=${t('serviceSettings.general.title')} description=${t('serviceSettings.general.description')}>
      <div class="form-field-row">
        <${NumberField} label=${t('serviceSettings.general.reopenWindowDays')} hint=${t('serviceSettings.general.reopenWindowHint')} value=${settings.reopenWindowDays}
          min=${0} max=${30} suffix=${t('serviceSettings.units.days')} disabled=${!canEdit} onChange=${(value) => set('reopenWindowDays', value)} />
        <${NumberField} label=${t('serviceSettings.general.autoCloseDays')} hint=${t('serviceSettings.general.autoCloseHint')} value=${settings.autoCloseResolvedAfterDays}
          min=${1} max=${30} suffix=${t('serviceSettings.units.days')} disabled=${!canEdit} onChange=${(value) => set('autoCloseResolvedAfterDays', value)} />
      </div>
      <${NumberField} label=${t('serviceSettings.general.consultationReminder')} hint=${t('serviceSettings.general.consultationReminderHint')} value=${settings.consultationReminderHours}
        min=${1} max=${168} suffix=${t('serviceSettings.units.hours')} disabled=${!canEdit} onChange=${(value) => set('consultationReminderHours', value)} />
      <div class="service-toggle-row">
        <${Toggle} value=${Boolean(settings.transferNoticeDefault)} disabled=${!canEdit} ariaLabel=${t('serviceSettings.general.transferNotice')}
          onChange=${(value) => set('transferNoticeDefault', value)} />
        <div><span class="form-field-label">${t('serviceSettings.general.transferNotice')}</span><span class="input-hint">${t('serviceSettings.general.transferNoticeHint')}</span></div>
      </div>
      <${FormError} message=${error} />
      ${canEdit
        ? html`<div class="service-inline-actions"><${Button} title=${t('serviceSettings.save')} accent="blue" size="small" isLoading=${isSaving} onClick=${() => save({
            reopenWindowDays: settings.reopenWindowDays, autoCloseResolvedAfterDays: settings.autoCloseResolvedAfterDays,
            consultationReminderHours: settings.consultationReminderHours, transferNoticeDefault: settings.transferNoticeDefault,
          })} /></div>`
        : null}
    <//>
    <${SettingsSection} title=${t('serviceSettings.general.numbersTitle')} description=${t('serviceSettings.general.numbersDescription')}>
      ${channels === null
        ? html`<${Skeleton} height=${60} />`
        : html`<ul class="service-settings-list">
            ${channels.map((channel) => {
              const purpose = channel.settings?.purpose ?? 'CAPTURE';
              const team = teams.find((candidate) => candidate.id === channel.settings?.serviceTeamId);
              return html`<li key=${channel.id}>
                <${Icon} name=${channel.type === 'EMAIL' ? 'IconMail' : 'IconBrandWhatsapp'} size=${16} />
                <span class="service-settings-list-title">${channel.settings?.displayPhoneNumber ?? channel.handle}</span>
                <span class="service-purpose-chip" data-purpose=${purpose}>${t(`serviceSettings.channelPurpose.values.${purpose}`)}</span>
                ${team ? html`<span class="input-hint">${t('serviceSettings.channelPurpose.queue', { team: team.name })}</span>` : null}
              </li>`;
            })}
          </ul>
          <${Link} href="/settings/channels" className="service-link">${t('serviceSettings.general.openChannels')}<//>`}
    <//>
  </div>`;
}

/** @param {{ canEdit: boolean }} props */
export function ServiceSettingsIdentityTab({ canEdit }) {
  const { settings, setSettings, error, isSaving, save } = useServiceDeskSettings();
  const [channelId, setChannelId] = useState(/** @type {string | null} */ (null));
  const [channels, setChannels] = useState(/** @type {any[]} */ ([]));
  const templates = useSendableTemplates(channelId);
  useEffect(() => {
    listChannels().then((data) => setChannels((data.channels ?? []).filter((channel) => channel.type === 'WHATSAPP'))).catch(() => setChannels([]));
  }, []);
  useEffect(() => {
    if (settings && channelId === null) setChannelId(settings.identityTemplate?.channelId ?? '');
  }, [settings]);
  if (!settings) return html`<${Skeleton} height=${200} radius=${8} />`;
  const identity = settings.identity ?? {};
  /** @param {string} key @param {number} value */
  const setIdentity = (key, value) => setSettings({ ...settings, identity: { ...identity, [key]: value } });
  const authenticationTemplates = (templates ?? []).filter((template) => String(template.category ?? '').toUpperCase() === 'AUTHENTICATION');
  const templateOptions = authenticationTemplates.length > 0 ? authenticationTemplates : templates ?? [];
  return html`<div class="service-settings-tab-body" data-testid="service-settings-identity">
    <${SettingsSection} title=${t('serviceSettings.identity.title')} description=${t('serviceSettings.identity.description')}>
      <div class="form-field-row">
        <${NumberField} label=${t('serviceSettings.identity.weakDataMaxAttempts')} value=${identity.weakDataMaxAttempts} min=${1} max=${10} disabled=${!canEdit}
          onChange=${(value) => setIdentity('weakDataMaxAttempts', value)} />
        <${NumberField} label=${t('serviceSettings.identity.weakDataWindowMinutes')} value=${identity.weakDataWindowMinutes} min=${5} max=${1440}
          suffix=${t('serviceSettings.units.minutes')} disabled=${!canEdit} onChange=${(value) => setIdentity('weakDataWindowMinutes', value)} />
      </div>
      <div class="form-field-row">
        <${NumberField} label=${t('serviceSettings.identity.codeTtlMinutes')} value=${identity.codeTtlMinutes} min=${1} max=${60} suffix=${t('serviceSettings.units.minutes')}
          disabled=${!canEdit} onChange=${(value) => setIdentity('codeTtlMinutes', value)} />
        <${NumberField} label=${t('serviceSettings.identity.codeMaxAttempts')} value=${identity.codeMaxAttempts} min=${1} max=${10} disabled=${!canEdit}
          onChange=${(value) => setIdentity('codeMaxAttempts', value)} />
      </div>
      <div class="form-field-row">
        <label class="form-field"><span class="form-field-label">${t('serviceSettings.identity.channel')}</span>
          <select class="text-input" value=${channelId ?? ''} disabled=${!canEdit} onChange=${(/** @type {Event} */ event) => {
            setChannelId(/** @type {HTMLSelectElement} */ (event.currentTarget).value);
            setSettings({ ...settings, identityTemplate: null });
          }}>
            <option value="">${t('serviceSettings.identity.noTemplate')}</option>
            ${channels.map((channel) => html`<option key=${channel.id} value=${channel.id}>${channel.settings?.displayPhoneNumber ?? channel.handle}</option>`)}
          </select>
        </label>
        <label class="form-field"><span class="form-field-label">${t('serviceSettings.identity.template')}</span>
          <select class="text-input" value=${settings.identityTemplate?.templateId ?? ''} disabled=${!canEdit || !channelId}
            onChange=${(/** @type {Event} */ event) => {
              const templateId = /** @type {HTMLSelectElement} */ (event.currentTarget).value;
              setSettings({ ...settings, identityTemplate: templateId && channelId ? { channelId, templateId } : null });
            }}>
            <option value="">${t('serviceSettings.identity.chooseTemplate')}</option>
            ${templateOptions.map((template) => html`<option key=${template.id} value=${template.id}>${template.name}</option>`)}
          </select>
        </label>
      </div>
      <span class="input-hint">${t('serviceSettings.identity.templateHint')}</span>
      <${FormError} message=${error} />
      ${canEdit
        ? html`<div class="service-inline-actions"><${Button} title=${t('serviceSettings.save')} accent="blue" size="small" isLoading=${isSaving}
            onClick=${() => save({ identity: settings.identity, identityTemplate: settings.identityTemplate ?? null })} /></div>`
        : null}
    <//>
  </div>`;
}

/** @param {{ canEdit: boolean }} props */
export function ServiceSettingsCsatTab({ canEdit }) {
  const [teams, setTeams] = useState(/** @type {any[] | null} */ (null));
  const [savingTeamId, setSavingTeamId] = useState(/** @type {string | null} */ (null));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  useEffect(() => {
    listServiceTeams().then(setTeams).catch((requestError) => setError(serviceErrorMessage(requestError)));
  }, []);
  /** @param {string} teamId @param {Record<string, any>} csat */
  const updateLocal = (teamId, csat) => setTeams((current) => (current ?? []).map((team) => (team.id === teamId ? { ...team, csat: { ...team.csat, ...csat } } : team)));
  /** @param {any} team */
  const save = async (team) => {
    setSavingTeamId(team.id);
    setError(null);
    try {
      await updateServiceTeamSettings(team.id, { csat: team.csat });
      showSuccessSnackbar(t('serviceSettings.saved'));
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setSavingTeamId(null);
    }
  };
  if (!teams) return html`<${Skeleton} height=${200} radius=${8} />`;
  return html`<div class="service-settings-tab-body" data-testid="service-settings-csat">
    <${SettingsSection} title=${t('serviceSettings.csat.title')} description=${t('serviceSettings.csat.description')}>
      ${teams.filter((team) => team.teamKind === 'STANDARD').map((team) => html`<div key=${team.id} class="service-settings-card">
        <div class="service-toggle-row">
          <${Toggle} value=${Boolean(team.csat?.enabled)} disabled=${!canEdit} ariaLabel=${t('serviceSettings.csat.enabled', { team: team.name })}
            onChange=${(value) => updateLocal(team.id, { enabled: value })} />
          <span class="service-settings-list-title">${team.name}</span>
        </div>
        ${team.csat?.enabled
          ? html`<label class="form-field"><span class="form-field-label">${t('serviceSettings.csat.question')}</span>
              <input class="text-input" value=${team.csat?.question ?? ''} maxLength=${1024} disabled=${!canEdit}
                onInput=${(/** @type {Event} */ event) => updateLocal(team.id, { question: /** @type {HTMLInputElement} */ (event.currentTarget).value })} /></label>
            <label class="form-field"><span class="form-field-label">${t('serviceSettings.csat.commentPrompt')}</span>
              <input class="text-input" value=${team.csat?.commentPrompt ?? ''} maxLength=${1024} disabled=${!canEdit}
                onInput=${(/** @type {Event} */ event) => updateLocal(team.id, { commentPrompt: /** @type {HTMLInputElement} */ (event.currentTarget).value })} /></label>
            <span class="input-hint">${t('serviceSettings.csat.buttonsHint')}</span>`
          : null}
        ${canEdit ? html`<div class="service-inline-actions"><${Button} title=${t('serviceSettings.save')} size="small" variant="secondary" isLoading=${savingTeamId === team.id} onClick=${() => save(team)} /></div>` : null}
      </div>`)}
      <p class="input-hint">${t('serviceSettings.csat.restrictedHint')}</p>
      <${FormError} message=${error} />
    <//>
  </div>`;
}
